import { useState, useMemo } from "react";
import type { AdminProduct } from "@/hooks/useAdminStore";
import type { Product } from "@/lib/api";
import ProductFormModal from "./ProductFormModal";

interface ProductsPanelProps {
  products: AdminProduct[];
  onToggle: (id: string, active: boolean) => void;
  onUpdate: (id: string, fields: Partial<AdminProduct>) => void;
  onAdd: (data: Omit<Product, "id" | "rating" | "reviewCount">) => void;
  onDelete: (id: string) => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  all: "All",
  clothes: "Clothes",
  shoes: "Shoes",
  bags: "Bags",
  nails: "Nails",
};

export default function ProductsPanel({
  products,
  onToggle,
  onUpdate,
  onAdd,
  onDelete,
}: ProductsPanelProps) {
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null | undefined>(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchCat = filterCat === "all" || p.category === filterCat;
      const matchStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && p.isActive) ||
        (filterStatus === "inactive" && !p.isActive);
      const matchSearch =
        !search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.category.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchStatus && matchSearch;
    });
  }, [products, filterCat, filterStatus, search]);

  const activeCount = products.filter((p) => p.isActive).length;
  const inactiveCount = products.filter((p) => !p.isActive).length;

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Products", value: products.length, icon: "ri-box-3-line" },
          { label: "Active", value: activeCount, icon: "ri-checkbox-circle-line", color: "text-emerald-600" },
          { label: "Hidden", value: inactiveCount, icon: "ri-eye-off-line", color: "text-[#aaa]" },
        ].map((stat) => (
          <div key={stat.label} className="bg-[#fafaf9] rounded-xl p-4 flex items-center gap-4">
            <div className={`w-9 h-9 flex items-center justify-center rounded-full bg-white ${stat.color ?? "text-[#555]"}`}>
              <i className={`${stat.icon} text-lg`}></i>
            </div>
            <div>
              <div className="text-2xl font-light text-[#1a1a1a]">{stat.value}</div>
              <div className="text-xs text-[#aaa]">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#bbb] text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full border border-[#e8e8e8] rounded-lg pl-9 pr-3 py-2.5 text-sm text-[#1a1a1a] outline-none focus:border-[#1a1a1a] transition-colors"
          />
        </div>

        {/* Category filter */}
        <div className="flex gap-1 bg-[#f5f5f3] rounded-lg p-1">
          {Object.keys(CATEGORY_LABELS).map((cat) => (
            <button
              key={cat}
              onClick={() => setFilterCat(cat)}
              className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap cursor-pointer transition-colors ${
                filterCat === cat
                  ? "bg-white text-[#1a1a1a] shadow-sm"
                  : "text-[#888] hover:text-[#555]"
              }`}
            >
              {CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex gap-1 bg-[#f5f5f3] rounded-lg p-1">
          {[["all", "All"], ["active", "Active"], ["inactive", "Hidden"]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilterStatus(val)}
              className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap cursor-pointer transition-colors ${
                filterStatus === val
                  ? "bg-white text-[#1a1a1a] shadow-sm"
                  : "text-[#888] hover:text-[#555]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Add button */}
        <button
          onClick={() => { setEditingProduct(undefined); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#1a1a1a] text-white rounded-lg px-4 py-2.5 text-sm hover:bg-[#333] cursor-pointer transition-colors whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line text-base"></i>
          </div>
          Add Product
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#f0f0f0] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#f0f0f0] bg-[#fafaf9]">
              <th className="text-left text-xs tracking-[0.1em] text-[#aaa] uppercase px-5 py-3 font-normal">Product</th>
              <th className="text-left text-xs tracking-[0.1em] text-[#aaa] uppercase px-3 py-3 font-normal">Category</th>
              <th className="text-left text-xs tracking-[0.1em] text-[#aaa] uppercase px-3 py-3 font-normal">Price</th>
              <th className="text-left text-xs tracking-[0.1em] text-[#aaa] uppercase px-3 py-3 font-normal">Tags</th>
              <th className="text-left text-xs tracking-[0.1em] text-[#aaa] uppercase px-3 py-3 font-normal">Status</th>
              <th className="text-right text-xs tracking-[0.1em] text-[#aaa] uppercase px-5 py-3 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-[#bbb] text-sm">
                  No products match your filters
                </td>
              </tr>
            ) : (
              filtered.map((product) => (
                <tr
                  key={product.id}
                  className={`border-b border-[#f8f8f8] hover:bg-[#fafaf9] transition-colors ${
                    !product.isActive ? "opacity-50" : ""
                  }`}
                >
                  {/* Product */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-[#f5f5f5]">
                        <img
                          src={product.images?.[0]}
                          alt={product.name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <div className="text-[#1a1a1a] font-medium text-sm leading-tight">{product.name}</div>
                        <div className="text-xs text-[#bbb] mt-0.5 truncate max-w-[200px]">
                          {product.shortDescription}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="px-3 py-3">
                    <span className="text-xs bg-[#f0f0f0] text-[#666] rounded-full px-2.5 py-1 capitalize">
                      {product.category}
                    </span>
                  </td>

                  {/* Price */}
                  <td className="px-3 py-3">
                    <div className="text-[#1a1a1a] font-medium">${product.price}</div>
                    {product.originalPrice && (
                      <div className="text-xs text-[#bbb] line-through">${product.originalPrice}</div>
                    )}
                  </td>

                  {/* Tags */}
                  <td className="px-3 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {product.isNew && (
                        <span className="text-xs bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">New</span>
                      )}
                      {product.isFeatured && (
                        <span className="text-xs bg-amber-50 text-amber-700 rounded-full px-2 py-0.5">Featured</span>
                      )}
                    </div>
                  </td>

                  {/* Status Toggle */}
                  <td className="px-3 py-3">
                    <button
                      onClick={() => onToggle(product.id, !product.isActive)}
                      className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${
                        product.isActive ? "bg-[#1a1a1a]" : "bg-[#ddd]"
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                          product.isActive ? "translate-x-5" : ""
                        }`}
                      />
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setEditingProduct(product); setShowModal(true); }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f0f0f0] cursor-pointer transition-colors text-[#888]"
                        title="Edit"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      {deleteConfirm === product.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => { onDelete(product.id); setDeleteConfirm(null); }}
                              className="text-xs text-red-500 hover:text-red-700 cursor-pointer px-1 whitespace-nowrap"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="text-xs text-[#aaa] hover:text-[#666] cursor-pointer px-1 whitespace-nowrap"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(product.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer transition-colors text-[#ccc] hover:text-red-400"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-[#bbb] text-right">
        Showing {filtered.length} of {products.length} products
      </div>

      {/* Modal */}
      {showModal && (
        <ProductFormModal
          product={editingProduct}
          onSave={onAdd}
          onUpdate={onUpdate}
          onClose={() => { setShowModal(false); setEditingProduct(null); }}
        />
      )}
    </div>
  );
}
